import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { List, Map as ImmutableMap } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { LogService } from '../../shared/log/log.service';
import { ConfigLookupType } from '../models/config/config-lookup-type';
import { ConfigResponse } from '../models/config/config-response';
import { GroupPayConfigHttpService } from './grouppay-config.http.service';

interface OoivCacheEntry {
  value: boolean;
  expiry: number;
}

export const CACHE_EXP_TIME = 60 * 60 * 1000; // 1 hour in milliseconds

@Injectable()
export class GroupPayConfigStore {
  private defaultEndDateDelayDaysSubject = new ReplaySubject<ConfigResponse>(1);

  private allSubject = new BehaviorSubject(ImmutableMap<ConfigLookupType, ConfigResponse>([]));

  private ooivCache = new Map<string, BehaviorSubject<OoivCacheEntry>>();

  constructor(private groupPayConfigHttpService: GroupPayConfigHttpService,
              private logService: LogService,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService) {
  }

  get defaultEndDateDelayDays(): Observable<ConfigResponse> {
    return this.defaultEndDateDelayDaysSubject.asObservable();
  }

  get defaultEndDateDelayDaysValue(): Observable<number> {
    return this.defaultEndDateDelayDays.map(configResponse => Number(configResponse.value));
  }

  private setDefaultEndDateDelayDays(configResponse: ConfigResponse) {
    this.defaultEndDateDelayDaysSubject.next(configResponse);
  }

  get allConfigurations(): Observable<ImmutableMap<ConfigLookupType, ConfigResponse>> {
    return this.allSubject.asObservable();
  }

  loadConfig(key: ConfigLookupType, chain: string, brand: string, propertyId: string, agencyId: string):
      Observable<ConfigResponse> {
    const response = this.groupPayConfigHttpService.getGroupPayConfigByValues(key, chain, brand, propertyId, agencyId)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GROUPPAY_CONFIG_SINGLE', {
          configValue: key.toString()
        }));
        this.logService.error(`Error retrieving configuration value (${key.toString()}): ${error}`);
        return Observable.throw(error);
      })
      .map(configResponse => {
        this.updateOneConfig(key, configResponse);
        return configResponse;
      });
    return response;
  }

  loadConfigBulk(requests: any[], key):
      Observable<ConfigResponse[]> {
    const response = this.groupPayConfigHttpService.getGroupPayConfigByValuesBulk(requests)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GROUPPAY_CONFIG_SINGLE', {
          configValue: key.toString()
        }));
        this.logService.error(`Error retrieving configuration value (${key.toString()}): ${error}`);
        return Observable.throw(error);
      })
      .map((configResponses: ConfigResponse[]) => {
        configResponses.forEach(configResponse => {
          this.updateOneConfig(key, configResponse);
        });
        return configResponses;
      });
    return response;
    }

  loadAllConfig(
    chain: string,
    brand: string,
    propertyId: string,
    agencyId: string
    ): Observable<ImmutableMap<ConfigLookupType, ConfigResponse>> {
    const response = this.groupPayConfigHttpService.getAllGroupPayConfigByValues(chain, brand, propertyId, agencyId)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GROUPPAY_CONFIG_ALL'));
        this.logService.error(`Error retrieveing all configuration values: ${error}`);
        return Observable.throw(error);
      })
      .map((responseMap: ImmutableMap<ConfigLookupType, ConfigResponse>) => {
        this.updateAllConfig(responseMap);
        return responseMap;
      });
    return response;
  }

  private updateOneConfig(key: ConfigLookupType, configResponse: ConfigResponse) {
    const mapConfig = this.allSubject.getValue();
    this.allSubject.next(mapConfig.set(key, configResponse));

    if (key === ConfigLookupType.DEFAULT_END_DATE_PLUS_DAYS) {
        this.setDefaultEndDateDelayDays(configResponse);
    }
  }

  private updateAllConfig(configMap: ImmutableMap<ConfigLookupType, ConfigResponse>) {
    this.allSubject.next(configMap);

    if (configMap.has(ConfigLookupType.DEFAULT_END_DATE_PLUS_DAYS)) {
      this.setDefaultEndDateDelayDays(configMap.get(ConfigLookupType.DEFAULT_END_DATE_PLUS_DAYS));
    }
    if (configMap.has(ConfigLookupType.AUTO_PAY)) {
      // TODO add this later
    }
  }

  public isOoivEnabled(agencyId: string, agencyCountry: string, vendorCountry: string): Observable<boolean> {
    this.logService.debug(`isOoivEnabled - agencyId: ${agencyId}, agencyCountry: ${agencyCountry}, vendorCountry: ${vendorCountry}`);

    let key = this.getOoivKey(agencyId, agencyCountry, vendorCountry);
    let cachedEntry = this.ooivCache.get(key);

    const entryExists = !!cachedEntry;
    const entryExpired = !!cachedEntry && !!cachedEntry.value && cachedEntry.value.expiry < Date.now();

    if (!entryExists) {
      cachedEntry = new BehaviorSubject<OoivCacheEntry | null>(null);
      this.ooivCache.set(key, cachedEntry);
    }

    if (!entryExists || entryExpired) {
      this.loadOoivConfig(null, agencyId, agencyCountry, vendorCountry).pipe(
        take(1),
        tap(
          (response: ConfigResponse) => {
            const newEntry = { value: response ? JSON.parse(response.value) : null, expiry: Date.now() + CACHE_EXP_TIME };
            cachedEntry!.next(newEntry);
          }
        ),
        catchError(
          (error: any) => {
            this.flashMessagesService.error('MESSAGES.ERROR.OOIV_CONFIG_VERIFICATION', `Error checking OOIV Config: ${error}`);
            return Observable.throw(error);
          }
        )).subscribe();
    }

    return cachedEntry.asObservable().pipe(
      filter((entry: OoivCacheEntry | null) => !!entry),
      map((entry: OoivCacheEntry) => entry.value)
    );
  }

  getOoivKey(agencyId: string, agencyCountry: string, vendorCountry: string): string {
    let key = '';
    key += !!agencyId ? agencyId : '';
    key += !!agencyCountry ? agencyCountry : '';
    key += !!vendorCountry ? vendorCountry : '';

    if (key && key.length > 0) {
      return key;
    } else {
      return 'null';
    }
  }

  loadOoivConfig(key: ConfigLookupType, agencyId: string, agencyCountry: string, vendorCountry: string):
      Observable<ConfigResponse> {
    const response = this.groupPayConfigHttpService.getGroupPayOoivConfigByValues(key, agencyId, agencyCountry, vendorCountry)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GROUPPAY_CONFIG_SINGLE', {
          configValue: key.toString()
        }));
        this.logService.error(`Error retrieving configuration value (${key.toString()}): ${error}`);
        return Observable.throw(error);
      })
      .map(configResponse => {
        this.updateOneConfig(key, configResponse);
        return configResponse;
      });
    return response;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/grouppay-config.store.ts